import React, { useState } from "react";
import { Link } from "react-router-dom";
import API from "../api/api";
import StepAddress from "../steps/StepAddress";
import StepPayment from "../steps/StepPayment";
import StepConfirmation from "../steps/StepConfirmation";
import { useLanguage } from "../i18n/LanguageContext";

const DELIVERY_FEE = 4.99;

function CheckoutPage({ cartItems, onOrderComplete }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [address, setAddress] = useState(null);
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const STEP_LABELS = [
    t("checkout.step.address"),
    t("checkout.step.payment"),
    t("checkout.step.confirmation"),
  ];

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const total = subtotal + DELIVERY_FEE;

  if (cartItems.length === 0 && step !== 3) {
    return (
      <div className="checkout-container">
        <h2>{t("checkout.title")}</h2>
        <p>{t("checkout.emptyCart")}</p>
        <Link to="/products" className="btn">
          {t("cart.shopHoney")}
        </Link>
      </div>
    );
  }

  const handleAddressSubmit = (form) => {
    setAddress(form);
    setStep(2);
  };

  const handlePaymentConfirm = async (paymentMethod) => {
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        customerName: address.name,
        email: address.email,
        address: `${address.street}, ${address.city}, ${address.zip}`,
        paymentMethod,
        items: cartItems.map((item) => ({
          productId: item.id,
          quantity: item.quantity,
        })),
      };
      const response = await API.post("/api/orders", payload);
      setOrder(response.data);
      onOrderComplete();
      setStep(3);
    } catch (err) {
      setError(err.response?.data || t("checkout.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="checkout-container">
      <h2>{t("checkout.title")}</h2>

      <div className="checkout-steps">
        {STEP_LABELS.map((label, i) => (
          <span
            key={label}
            className={`checkout-step-pill ${step === i + 1 ? "active" : ""} ${
              step > i + 1 ? "done" : ""
            }`}
          >
            {i + 1} {label}
          </span>
        ))}
      </div>

      {error && <p className="checkout-error">{String(error)}</p>}

      {step === 1 && (
        <StepAddress initialValue={address} onSubmit={handleAddressSubmit} />
      )}

      {step === 2 && (
        <StepPayment
          total={total}
          submitting={submitting}
          onConfirm={handlePaymentConfirm}
          onBack={() => setStep(1)}
        />
      )}

      {step === 3 && order && (
        <StepConfirmation order={order} address={address} />
      )}
    </div>
  );
}

export default CheckoutPage;
